<script>
import FormModal from "@/components/form-modal.vue";
import MasterDetail from "@/components/master-detail.vue";
import { mapGetters } from "vuex";
import { AnyAdmin } from "@/core/enums/user-types";
import { TiposResponsaveisEnum } from "../responsaveis/responsaveis.types";
import responsaveisService from "@/api/ativacaoped/responsaveis.service";
import businessUnitsService from "@/api/ativacaoped/business-units.service";
import niveisHierarquicosService from "@/api/ativacaoped/niveis-hierarquicos.service";
import businessPlansService from "@/api/ativacaoped/business-plans.service";
import { createServiceResource } from "@/api/ativacaoped/resource-adapter";

const createEmptyFormData = () => ({
  nivel_hierarquico_id: null,
  business_unit_id: null,
  nome: "",
  email: ""
});

export default {
  components: {
    FormModal,
    MasterDetail
  },
  data() {
    return {
      createModal: {
        data: createEmptyFormData(),
        opened: false,
        title: ""
      },
      enabledEmail: false,
      opts: {
        businessUnits: [],
        niveisHierarquicos: [],
        acessos: [
          { id: true, nome: "Sim" },
          { id: false, nome: "Não" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    actionBarButtons() {
      const show = this.podeEditar;
      const buttons = [
        {
          text: "Adicionar",
          icon: "mdi-plus-box-outline",
          show,
          action: () => {
            this.enabledEmail = true;
            this.createModal.data = createEmptyFormData();
            this.createModal.title = "Cadastro de Responsável";
            this.createModal.opened = true;
          }
        }
      ];

      return buttons.filter(({ show }) => show);
    },
    cols() {
      const self = this;

      return [
        {
          key: "usuario",
          name: "Possui acesso?",
          type: this.$fieldTypes.SELECT,
          rel: { to: "acessos", key: "id", name: "nome" },
          hideInform: true,
          filterable: true,
          width: "50px"
        },
        {
          key: "nivel_hierarquico_id",
          name: "Nível hierarquico",
          rules: [{ rule: "required" }],
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "niveisHierarquicos", key: "id", name: "nome" },
          filterable: true,
          colSize: 6
        },
        {
          key: "business_unit_id",
          name: "Business Unit",
          rules: [{ rule: "required" }],
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "businessUnits", key: "id", name: "label" },
          filterable: true,
          colSize: 6
        },
        {
          key: "nome",
          name: "Nome",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "required" }],
          filterable: true
        },
        {
          key: "email",
          name: "E-mail",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "required" }],
          editable: self.enabledEmail,
          filterable: true
        }
      ];
    },
    /**
     * Resource service-backed consumido pelo `MasterDetail` (`get` na grid e
     * `save` no formulário interno de edição — a grid mantém `canEdit` padrão).
     * Criar passa pelo modal da tela (`postResponsavel`) e a exclusão está
     * desabilitada (`:canDelete="false"`), então `delete` não é exposto.
     * Substitui o `:resourceUrl`, que usava o `apiResource` inline do mixin.
     *
     * @returns {Object} Resource no contrato do `apiResource`
     */
    customResource() {
      // Ver nota em `divisoes.vue`: ler `clientId` aqui mantém o reload da grid
      // pelo watcher de `customResource` do `MasterDetail`.
      const clientId = this.clientId;

      return createServiceResource({
        notify: this.notify,
        get: ({ query }) => responsaveisService.getResponsaveis(clientId, query),
        save: (data, id) =>
          id
            ? responsaveisService.putResponsavel(clientId, id, data)
            : responsaveisService.postResponsavel(clientId, data)
      });
    },
    /**
     * Quem pode criar e editar responsáveis: Galapos (Administrador/Consultor) ou
     * responsável com nível hierárquico Administrador — a mesma regra do "Adicionar".
     * @type {boolean}
     * @readonly
     */
    podeEditar() {
      return AnyAdmin.includes(this.user.tipo_usuario) || this.hasAdminRole;
    },
    /**
     * Determina se o usuário logado é um responsável com nível hierárquico
     * Administrador (regra genérica de permissão, não específica de cliente).
     * @type {boolean}
     * @readonly
     */
    hasAdminRole() {
      return (
        this.user.nivel_hierarquico?.tipo_id ===
        TiposResponsaveisEnum.ADMINISTRADOR
      );
    }
  },
  async created() {
    if (this.clientId === 0) {
      this.goToHome();
      return;
    }

    const promises = [this.getBusinessUnits(), this.getNiveisHierarquicos()];
    await Promise.all(promises);
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
        return;
      }

      this.getBusinessUnits();
      this.getNiveisHierarquicos();
    }
  },
  methods: {
    async doExport() {
      try {
        const response = await responsaveisService.getExportResponsaveis(
          this.clientId
        );

        if (!response.url) {
          throw new Error();
        }

        const a = document.createElement("a");
        a.href = response.url;
        a.target = "_blank";
        a.click();
        a.remove();
      } catch {
        this.notify({
          message: "Não foi possível baixar o modelo. Tente novamente."
        });
      }
    },
    doImport() {
      this.$refs.inputFile?.click();
    },
    async doSave(data, close) {
      try {
        await responsaveisService.postResponsavel(this.clientId, data);

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Registro adicionado com sucesso",
          text: `Um novo responsável foi adicionado com sucesso.`
        });
        this.$refs.masterDetail?.doLoad();

        if (close) {
          close();
        }
      } catch (error) {
        this.notify(error);
      }
    },
    onLoadedRows(rows) {
      if (!Array.isArray(rows)) {
        return;
      }

      rows.forEach(item => {
        item.usuario = !!item.usuario_id;
      });
    },
    async getBusinessUnits() {
      try {
        const response = await businessUnitsService.getSelecaoBusinessUnits(
          this.clientId
        );
        this.opts.businessUnits = response.map(bu => ({
          ...bu,
          label: `${bu.nome}${bu.divisao?.nome ? ` (${bu.divisao.nome})` : ''}`
        }));
      } catch (error) {
        this.notify(error);
        this.opts.businessUnits = [];
      }
    },
    async getNiveisHierarquicos() {
      try {
        const response = await niveisHierarquicosService.getSelecaoNiveisHierarquicos(
          this.clientId
        );
        this.opts.niveisHierarquicos = response.sort(
          ({ nome: a }, { nome: b }) => a.localeCompare(b)
        );
      } catch (error) {
        this.notify(error);
        this.opts.niveisHierarquicos = [];
      }
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    },
    async onInputChange(event) {
      const [file] = Array.from(event.target.files);
      const MAX_FILE_SIZE = 10; // 10 MegaBytes

      if (file.size > MAX_FILE_SIZE * 1024 * 1024) {
        this.notify({
          error: `O arquivo ${file.name} excedeu o limite de ${MAX_FILE_SIZE}MB e não foi importado.`
        });
        return;
      }

      try {
        this.$root.$emit("addLoadingProcess", "responsaveisimport");
        const formData = new FormData();
        formData.append("arquivo", file);
        formData.append("tipo", "responsaveis");

        await businessPlansService.importarBusinessPlans(this.clientId, formData);

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Importação concluída",
          text: `O arquivo está na fila para ser analisado e dentro de instantes você receberá uma notificação do sistema.`
        });
        this.$refs.masterDetail?.doLoad();
      } catch (error) {
        this.notify(error);
      } finally {
        this.$root.$emit("removeLoadingProcess", "responsaveisimport");
      }

      event.target.value = null; // Importante para poder voltar a anexar mais arquivos
    }
  }
};
</script>

<template>
  <MasterDetail
    ref="masterDetail"
    :actionBarButtons="actionBarButtons"
    :canDelete="false"
    :canEditByRole="podeEditar"
    :cols="cols"
    :customResource="customResource"
    @loadedRows="onLoadedRows"
    formTitle="Responsável"
    :hasExportCSV="false"
    :hasExportXLSX="false"
    :hasNewButton="false"
    :opts="opts"
    serverPagination
    @onOpenFormDialog="enabledEmail = !$event.id"
  >
    <template v-slot:item.usuarionome="{ item }">
      <div class="mx-auto" style="display: table">
        <v-icon :color="item.usuario ? '#135153' : '#EC4C47'">
          {{ item.usuario ? "mdi-check-circle" : "mdi-close-circle" }}
        </v-icon>
      </div>
    </template>

    <input
      ref="inputFile"
      accept=".xlsx"
      class="input-file"
      type="file"
      @change="onInputChange"
    />

    <div class="d-flex">
      <v-spacer></v-spacer>
      <div
        aria-label="exportButton"
        class="table-v-action-button mr-3 pt-1"
        @click="doExport()"
      >
        <v-icon>mdi-download-box-outline</v-icon> Exportar modelo
      </div>
      <div
        aria-label="importButton"
        class="table-v-action-button mr-3 pt-1"
        @click="doImport()"
      >
        <v-icon>mdi-upload-box-outline</v-icon> Importações
      </div>
    </div>

    <FormModal
      v-model="createModal.opened"
      :cols="cols"
      :opts="opts"
      :title="createModal.title"
      :value.sync="createModal.data"
      @save="doSave"
    />
  </MasterDetail>
</template>

<style lang="scss" scoped>
.input-file {
  display: none;
}
</style>
